import {
  CertificateDesignerStore,
  CertificateOrientation,
  CertificatePaperSize,
  CertificateTemplate,
  getJsPdfPageOptions,
  getPageSize,
  ReportChartPreview,
  ReportChecklistPreview,
} from "../certificatePrint/certificateTypes";
import { cloneCertificateTemplate } from "../certificatePrint/certificateTemplates";
import { printCertificateSheet } from "../certificatePrint/certificateStorage";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  DEFAULT_PATIENT_REPORT_TEMPLATE_ID,
  DEFAULT_PATIENT_REPORT_TEMPLATES,
  getDefaultPatientReportTemplateById,
} from "./patientReportTemplates";
import { REPORT_SECTIONS } from "./patientReportDefaults";

const STORAGE_KEY = "our_dental.patientReportPrint.templates.v15";

export function createPatientReportTemplateId() {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function builtinStore(): CertificateDesignerStore {
  return {
    templates: DEFAULT_PATIENT_REPORT_TEMPLATES.map((template) =>
      cloneCertificateTemplate(template, { id: template.id, name: template.name })
    ),
    activeId: DEFAULT_PATIENT_REPORT_TEMPLATE_ID,
    defaultId: DEFAULT_PATIENT_REPORT_TEMPLATE_ID,
  };
}

const TABLE_FIELD_KEYS = new Set([
  "complainTable",
  "investigationTable",
  "diagnoseTable",
  "treatmentTable",
  "prescriptionTable",
  "paymentTable",
]);

const REQUIRED_REPORT_FIELDS = [
  "reportDescriptionTitle",
  "reportDescription",
  "signature",
  "section-8-title",
  "section-8",
] as const;

function baseElementId(id: string) {
  return String(id).split("__")[0];
}

function ensureDescriptionAndSignature(
  elements: CertificateTemplate["elements"],
  builtin: CertificateTemplate | undefined
): CertificateTemplate["elements"] {
  if (!builtin) return elements;
  const ids = new Set(elements.map((el) => baseElementId(el.id)));
  const missing = REQUIRED_REPORT_FIELDS.filter((id) => !ids.has(id));
  if (!missing.length) return elements;
  const missingSet = new Set<string>(missing);
  const extras = builtin.elements
    .filter((el) => missingSet.has(el.id))
    .map((el) => ({ ...el }));
  return [...elements, ...extras];
}

function ensureLetterheadElement(
  elements: CertificateTemplate["elements"],
  builtin: CertificateTemplate | undefined
): CertificateTemplate["elements"] {
  if (elements.some((el) => baseElementId(el.id) === "letterhead")) {
    return elements;
  }
  const source =
    builtin?.elements.find((el) => el.id === "letterhead") ||
    DEFAULT_PATIENT_REPORT_TEMPLATES[0].elements.find((el) => el.id === "letterhead");
  if (!source) return elements;
  return [{ ...source }, ...elements];
}

/** Append missing built-in fields without replacing the saved layout. */
function ensureBuiltinElementsById(
  elements: CertificateTemplate["elements"],
  builtin: CertificateTemplate | undefined,
  ids: string[]
): CertificateTemplate["elements"] {
  if (!builtin || !ids.length) return elements;
  const have = new Set(elements.map((el) => baseElementId(el.id)));
  const extras = builtin.elements
    .filter((el) => ids.includes(el.id) && !have.has(el.id))
    .map((el) => ({ ...el }));
  return extras.length ? [...elements, ...extras] : elements;
}

function hydrateTemplate(raw: CertificateTemplate): CertificateTemplate {
  const builtin =
    getDefaultPatientReportTemplateById(raw.id) ||
    (raw.page?.size === "A5"
      ? getDefaultPatientReportTemplateById("a5-patient-report")
      : getDefaultPatientReportTemplateById(DEFAULT_PATIENT_REPORT_TEMPLATE_ID));
  const fallback = DEFAULT_PATIENT_REPORT_TEMPLATES[0];
  const source = builtin || fallback;

  const mapped =
    Array.isArray(raw.elements) && raw.elements.length > 0
      ? raw.elements.map((el) => {
          if (el.fieldKey === "diagnosisChart" || baseElementId(el.id) === "section-3") {
            return {
              ...el,
              type: "chart" as const,
              fieldKey: el.fieldKey || "diagnosisChart",
            };
          }
          if (
            el.fieldKey === "medicalHistoryChecklist" ||
            baseElementId(el.id) === "section-6"
          ) {
            return {
              ...el,
              type: "checklist" as const,
              fieldKey: el.fieldKey || "medicalHistoryChecklist",
            };
          }
          return TABLE_FIELD_KEYS.has(el.fieldKey || "")
            ? { ...el, type: "table" as const }
            : el;
        })
      : source.elements.map((el) => ({ ...el }));

  const elements = ensureBuiltinElementsById(
    ensureLetterheadElement(
      ensureDescriptionAndSignature(mapped, source),
      source
    ),
    source,
    ["headerRule", "section-3", "section-3-title", "section-6", "section-6-title"]
  );

  return {
    id: raw.id || `tpl-${Date.now()}`,
    name: raw.name || source.name,
    page: {
      ...source.page,
      ...raw.page,
      letterheadSrc: raw.page?.letterheadSrc || "",
    },
    pageCount: Math.max(
      1,
      Number(raw.pageCount) || 1,
      ...elements.map((el) => Math.floor(Number(el.pageIndex) || 0) + 1)
    ),
    elements: elements.map((el) => ({
      ...el,
      pageIndex: Math.max(0, Math.floor(Number(el.pageIndex) || 0)),
    })),
  };
}

export function isPatientReportDesignerStore(raw: unknown): raw is CertificateDesignerStore {
  return Boolean(
    raw &&
      typeof raw === "object" &&
      Array.isArray((raw as CertificateDesignerStore).templates)
  );
}

export function storeFromLegacyPrint(raw: any): CertificateDesignerStore {
  const store = builtinStore();
  const template = store.templates[0];
  if (raw?.paperSize === "A5") {
    template.page.size = "A5";
  }
  if (Array.isArray(raw?.sections) && raw.sections.length) {
    const keys = raw.sections.map(String);
    template.elements = template.elements.map((el) =>
      el.id.startsWith("section-")
        ? { ...el, visible: keys.includes(el.id.replace("section-", "")) }
        : el
    );
  }
  return store;
}

export function hydratePatientReportStore(raw: unknown): CertificateDesignerStore {
  if (isPatientReportDesignerStore(raw) && raw.templates.length) {
    const templates = raw.templates.map((item) =>
      fillEmptyReportPages(hydrateTemplate(item))
    );
    const activeId = templates.some((item) => item.id === raw.activeId)
      ? raw.activeId
      : templates[0].id;
    const defaultId = templates.some((item) => item.id === raw.defaultId)
      ? raw.defaultId
      : templates[0].id;
    return { templates, activeId, defaultId };
  }
  if (raw && typeof raw === "object") {
    return storeFromLegacyPrint(raw);
  }
  return builtinStore();
}

export function loadPatientReportStore(): CertificateDesignerStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return builtinStore();
    return hydratePatientReportStore(JSON.parse(raw));
  } catch {
    return builtinStore();
  }
}

export function savePatientReportStore(store: CertificateDesignerStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

export function findPatientReportTemplate(
  templates: CertificateTemplate[],
  id: string
): CertificateTemplate {
  return templates.find((item) => item.id === id) ?? templates[0];
}

export function getDefaultPatientReportBuiltin(id: string) {
  return (
    getDefaultPatientReportTemplateById(id) ??
    getDefaultPatientReportTemplateById(DEFAULT_PATIENT_REPORT_TEMPLATE_ID)
  );
}

export function reportPageCount(template: CertificateTemplate) {
  return Math.max(
    1,
    template.pageCount || 1,
    ...template.elements.map((el) => Math.floor(Number(el.pageIndex) || 0) + 1)
  );
}

const REPEAT_ON_NEW_PAGE = new Set([
  "letterhead",
  "logo",
  "clinicName",
  "clinicAddress",
  "clinicPhone",
  "generatedAt",
  "headerRule",
  "footerRule",
  "reportFooter",
  "reportPage",
  "signature",
]);

const KEEP_ON_FIRST_PAGE = new Set(["1", "3"]);

function elementPageIndex(el: { pageIndex?: number }) {
  return Math.max(0, Math.floor(Number(el.pageIndex) || 0));
}

function sectionGroupKey(el: { id: string }) {
  const match = baseElementId(el.id).match(/^section-(\d+)/);
  return match ? match[1] : null;
}

function pageHasBody(template: CertificateTemplate, pageIndex: number) {
  return template.elements.some(
    (el) => elementPageIndex(el) === pageIndex && sectionGroupKey(el)
  );
}

function cloneLetterhead(template: CertificateTemplate, pageIndex: number) {
  return template.elements
    .filter((el) => {
      const id = baseElementId(el.id);
      return elementPageIndex(el) === 0 && REPEAT_ON_NEW_PAGE.has(id);
    })
    .map((el) => ({
      ...el,
      id: `${el.id}__p${pageIndex}`,
      pageIndex,
      content:
        baseElementId(el.id) === "reportPage"
          ? `Confidential  ·  Page ${pageIndex + 1}`
          : el.content,
    }));
}

function letterheadBottom(clones: CertificateTemplate["elements"]) {
  const tops = clones.filter(
    (el) => el.section !== "footer" && !String(el.id).toLowerCase().includes("footer")
  );
  return Math.max(40, ...tops.map((el) => el.y + el.height + 8));
}

function overflowKeys(groups: string[], targetPage: number) {
  if (targetPage === 1) {
    const extra = groups.filter((key) => !KEEP_ON_FIRST_PAGE.has(key));
    return extra.length ? extra : groups.slice(1);
  }
  const split = Math.max(1, Math.ceil(groups.length / 2));
  return groups.slice(split);
}

function moveOverflowOntoPage(
  template: CertificateTemplate,
  targetPage: number,
  extraElements: CertificateTemplate["elements"] = []
): CertificateTemplate {
  const clones = extraElements.length
    ? extraElements
    : template.elements.filter((el) => elementPageIndex(el) === targetPage);
  const headerBottom = letterheadBottom(clones);

  let sourcePage = targetPage - 1;
  while (sourcePage > 0 && !pageHasBody(template, sourcePage)) {
    sourcePage -= 1;
  }

  const sourceBody = template.elements.filter(
    (el) => elementPageIndex(el) === sourcePage && sectionGroupKey(el)
  );
  const groups = Array.from(
    new Set(sourceBody.map((el) => sectionGroupKey(el) as string))
  ).sort((a, b) => {
    const ya = Math.min(
      ...sourceBody.filter((el) => sectionGroupKey(el) === a).map((el) => el.y)
    );
    const yb = Math.min(
      ...sourceBody.filter((el) => sectionGroupKey(el) === b).map((el) => el.y)
    );
    return ya - yb;
  });

  const moveSet = new Set(overflowKeys(groups, targetPage));
  const moving = sourceBody
    .filter((el) => moveSet.has(sectionGroupKey(el) || ""))
    .sort((a, b) => a.y - b.y || a.x - b.x);
  const origin = moving[0]?.y ?? headerBottom;
  const movedIds = new Set(moving.map((el) => el.id));
  const restacked = moving.map((el) => ({
    ...el,
    pageIndex: targetPage,
    y: headerBottom + (el.y - origin),
  }));

  return {
    ...template,
    elements: [
      ...template.elements.filter((el) => !movedIds.has(el.id)),
      ...extraElements,
      ...restacked,
    ],
  };
}

function fillEmptyReportPages(template: CertificateTemplate): CertificateTemplate {
  let next = template;
  const count = reportPageCount(next);
  for (let pageIndex = 1; pageIndex < count; pageIndex += 1) {
    if (!pageHasBody(next, pageIndex)) {
      next = moveOverflowOntoPage(next, pageIndex);
    }
  }
  return next;
}

export function addPatientReportPage(template: CertificateTemplate): CertificateTemplate {
  const count = reportPageCount(template);
  const last = count - 1;
  if (last > 0 && !pageHasBody(template, last)) {
    return moveOverflowOntoPage(template, last);
  }
  if (count >= 6) return template;
  const clones = cloneLetterhead(template, count);
  return {
    ...moveOverflowOntoPage(template, count, clones),
    pageCount: count + 1,
  };
}

export function removePatientReportPage(template: CertificateTemplate): CertificateTemplate {
  const count = reportPageCount(template);
  if (count <= 1) return template;
  const last = count - 1;
  const cloneSuffix = `__p${last}`;
  return {
    ...template,
    pageCount: last,
    elements: template.elements
      .filter((el) => !String(el.id).includes(cloneSuffix))
      .map((el) =>
        elementPageIndex(el) === last ? { ...el, pageIndex: last - 1 } : el
      ),
  };
}
export function applyPatientReportGenerateOptions(
  template: CertificateTemplate,
  sections: string[],
  paperSize: "A4" | "A5" | "halfLetter"
): CertificateTemplate {
  return {
    ...template,
    page: { ...template.page, size: paperSize, showSafeArea: false },
    elements: template.elements.map((el) => {
      if (!el.id.startsWith("section-")) return { ...el };
      const key = el.id.replace(/^section-/, "").replace(/-title$/, "");
      return { ...el, visible: sections.includes(key) };
    }),
  };
}

export function visibleReportSections(template: CertificateTemplate): string[] {
  const keys = REPORT_SECTIONS.map((item) => item.KEY);
  return template.elements
    .filter((el) => el.visible && el.id.startsWith("section-"))
    .map((el) => el.id.replace("section-", "").replace(/-title$/, ""))
    .filter((key) => keys.includes(key as (typeof keys)[number]));
}

export { printCertificateSheet as printPatientReportSheet };

export type PatientReportPayload = {
  clinic?: {
    clinicName?: string;
    title?: string;
    address?: string;
    tpNo?: string;
    phoneTwo?: string;
    printImages?: string;
  };
  patient?: any;
  complain?: any[];
  investigation?: any[];
  diagnose?: any[];
  treatment?: any[];
  payments?: any[];
  prescription?: any[];
};

const MEDICAL_HISTORY_FIELDS = [
  { key: "allergies", label: "Allergies" },
  { key: "diabetesMellitusDrugs", label: "Diabetes mellitus – Drugs" },
  { key: "hypertensionDrugs", label: "Hypertension – Drugs" },
  { key: "dyslipidemiaDrugs", label: "Dyslipidemia" },
  { key: "gastricProblem", label: "Gastric problem" },
  { key: "highBP", label: "High BP" },
  { key: "diabetes", label: "Diabetes" },
  { key: "monthlyInjection", label: "Monthly injection" },
  { key: "pregnancyBreastFeeding", label: "Pregnancy / breast feeding" },
  { key: "coughColdFever", label: "Cough / cold / fever" },
  { key: "chestpainHeartProblem", label: "Chest pain / heart" },
  { key: "epilepsy", label: "Epilepsy" },
  { key: "previousExtraction", label: "Previous extraction" },
  { key: "previoussurgeries", label: "Previous surgeries" },
  { key: "covid193Doses", label: "COVID-19 (3 doses)" },
  { key: "drugHistory", label: "Drug history" },
  { key: "dietaryHistory", label: "Dietary history" },
  { key: "brushingHistory", label: "Brushing history" },
  { key: "medicalHistoryNote", label: "Medical history (note)" },
  { key: "oralHygieneHistory", label: "Oral hygiene history" },
  { key: "habitHistory", label: "Habit history" },
  { key: "familyHistory", label: "Family history" },
  { key: "socialHistory", label: "Social history" },
  { key: "extraOralExamination", label: "Extra-oral examination" },
  { key: "intraOralExamination", label: "Intra-oral examination" },
];

function reportText(value: unknown, fallback = "—") {
  const raw = String(value ?? "").trim();
  return raw || fallback;
}

function padNo(index: number) {
  return String(index + 1).padStart(2, "0");
}

function formatReportDate(value: unknown) {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}/${m}/${d}`;
}

function formatMoney(value: unknown) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("en-US");
}

function ageFromDob(dob?: unknown) {
  if (!dob) return "—";
  const date = new Date(String(dob));
  if (Number.isNaN(date.getTime())) return "—";
  const years = Math.floor(
    (Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
  );
  return Number.isFinite(years) && years >= 0 ? `${years} yrs` : "—";
}

function isYesStatus(status: unknown) {
  return status === true || status === "true" || status === 1 || status === "1";
}

function formatNote(field: any) {
  if (field == null) return "";
  if (typeof field !== "object") {
    const textValue = String(field).trim();
    return textValue && textValue !== "N/A" && textValue !== "-" ? textValue : "";
  }
  const parts: string[] = [];
  if (field.note) {
    const note = String(field.note).trim();
    if (note && note !== "N/A" && note !== "-") parts.push(note);
  }
  if (Array.isArray(field.items)) {
    field.items.forEach((item: any) => {
      if (!item) return;
      const bits = [item.label || item.id];
      if (item.note) bits.push(String(item.note).trim());
      if (item.dosage) bits.push(`dose: ${String(item.dosage).trim()}`);
      const line = bits.filter(Boolean).join(" — ");
      if (line) parts.push(line);
    });
  }
  return parts.join("; ");
}

function tableRows(rows: string[][], columns: number) {
  if (rows.length) return rows;
  return [Array.from({ length: columns }, () => "—")];
}

function flattenTreatments(rows: any[]) {
  const out: any[] = [];
  (rows || []).forEach((row) => {
    out.push(row);
    (row?.subTreatment || []).forEach((sub: any) => out.push(sub));
  });
  return out;
}

export function buildPatientReportPrintData(payload: PatientReportPayload) {
  const clinic = payload.clinic || {};
  const patient = payload.patient || {};
  const phones = [clinic.tpNo, clinic.phoneTwo].filter(Boolean).join("  |  ");
  const generatedAt = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const previewData: Record<string, string> = {
    clinicName: reportText(clinic.clinicName || clinic.title, "Dental Clinic").toUpperCase(),
    clinicAddress: reportText(clinic.address, ""),
    clinicPhone: reportText(phones, ""),
    generatedAt,
    patientName: reportText(patient.fullName),
    patientGender: reportText(patient.gender),
    patientAge: ageFromDob(patient.dob),
    patientDob: formatReportDate(patient.dob),
    patientReg: reportText(patient.regNo),
    patientMobile: reportText(patient.mobileNo),
    patientAddress: reportText(patient.address, ""),
  };

  const complain = payload.complain || [];
  const investigation = payload.investigation || [];
  const diagnose = payload.diagnose || [];
  const treatments = flattenTreatments(payload.treatment || []);
  const payments = payload.payments || [];
  const prescription = payload.prescription || [];

  const previewTables: Record<string, { columns: string[]; rows: string[][] }> = {
    complainTable: {
      columns: ["NO", "DESCRIPTION", "TOOTH"],
      rows: tableRows(
        complain.map((row, index) => [
          padNo(index),
          reportText(row.description),
          reportText(row.toothNo),
        ]),
        3
      ),
    },
    investigationTable: {
      columns: ["NO", "INVESTIGATION", "TOOTH", "DESCRIPTION"],
      rows: tableRows(
        investigation.map((row, index) => [
          padNo(index),
          reportText(row.investigation),
          reportText(row.toothNo),
          reportText(row.description),
        ]),
        4
      ),
    },
    diagnoseTable: {
      columns: ["NO", "DIAGNOSIS", "TOOTH", "NOTE", "PRIORITY"],
      rows: tableRows(
        diagnose.map((row, index) => [
          padNo(index),
          reportText(row.diagnose),
          reportText(row.toothNo),
          reportText(row.note),
          reportText(row.priority),
        ]),
        5
      ),
    },
    treatmentTable: {
      columns: ["NO", "DATE", "TREATMENT", "TOOTH", "QTY", "PRICE", "STATUS"],
      rows: tableRows(
        treatments.map((row, index) => [
          padNo(index),
          formatReportDate(row.createdAt),
          reportText(row.treatment),
          reportText(row.toothNo),
          reportText(row.quantity),
          formatMoney(row.price),
          reportText(row.status),
        ]),
        7
      ),
    },
    prescriptionTable: {
      columns: ["NO", "DRUGS", "DOSE", "FREQUENCY", "DURATION"],
      rows: tableRows(
        prescription.map((row, index) => [
          padNo(index),
          reportText(row.drugsId?.drugs || row.drugs),
          reportText(row.doseId || row.dose),
          reportText(row.frequency),
          reportText(row.duration),
        ]),
        5
      ),
    },
    paymentTable: {
      columns: ["NO", "DATE", "INVOICE", "TOTAL", "DISCOUNT", "CASH", "CARD"],
      rows: tableRows(
        payments.map((row, index) => [
          padNo(index),
          formatReportDate(row.createdAt),
          reportText(row.invoiceNo),
          formatMoney(row.netotal),
          formatMoney(row.discount ?? 0),
          formatMoney(row.cash ?? 0),
          formatMoney(row.card ?? 0),
        ]),
        7
      ),
    },
  };

  const previewCharts: Record<string, ReportChartPreview> = {
    diagnosisChart: {
      marked: diagnose
        .filter((row) => row?.toothNo != null && row?.isTop !== false)
        .map((row) => String(row.toothNo)),
      actions: diagnose
        .filter((row) => row?.toothNo != null)
        .map((row) => ({
          toothNo: row.toothNo,
          surface: row.surface,
          color: row.color || "#ef4444",
          hasSurface: Boolean(row.hasSurface),
          isTop: row.isTop !== false,
        })),
    },
  };

  const previewChecklists: Record<string, ReportChecklistPreview> = {
    medicalHistoryChecklist: {
      items: MEDICAL_HISTORY_FIELDS.map((field) => ({
        label: field.label,
        checked: isYesStatus(patient?.[field.key]?.status ?? patient?.[field.key]),
        note: formatNote(patient?.[field.key]),
      })),
    },
  };

  return { previewData, previewTables, previewCharts, previewChecklists };
}

export async function downloadPatientReportPdf(options: {
  stack: HTMLElement;
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
  filename: string;
}) {
  const papers = Array.from(
    options.stack.querySelectorAll<HTMLElement>(".certificate-paper")
  );
  const targets = papers.length ? papers : [options.stack];
  const page = getPageSize(options.orientation, options.size);
  const pdfOpts = getJsPdfPageOptions(options.orientation, options.size);
  const pdf = new jsPDF({
    orientation: pdfOpts.orientation,
    unit: pdfOpts.unit,
    format: pdfOpts.format,
  });

  for (let index = 0; index < targets.length; index += 1) {
    const canvas = await html2canvas(targets[index], {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
    if (index > 0) pdf.addPage();
    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      0,
      page.widthMm,
      page.heightMm,
      "",
      "FAST"
    );
  }

  pdf.save(options.filename);
}

